import { action } from "typesafe-actions";
import {
  IBusinessAdditionalInsured,
  IBusinessAttachment,
  IClaims,
  ICustomerInfo,
  IPolicyAdmin,
  IPolicyAdminManager,
  IPremium,
  IPriorPolicy,
  initialBasicDetails,
} from "./businessPolicyBasicDetails.types";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../../initialStoreState";
import { AnyAction } from "redux";
import { api } from "../../../api/api";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../messages/messagesActions";
import { ERROR_MESSAGES, LoadState } from "../../../constants/enums";
import { getUniqueId } from "../../../helpers";
import { makeApiCall } from "../../../helpers/postRequest";
import { uploadFile, uploadMultipleFile } from "../../../features/FileUpload/utils";
import { IFileUpload } from "../../../features/FileUpload/components/FileUpload.type";
import { buildBinderRequest } from "../leadBinder/leadBinderEndpoints";

// Feature: lead-binder-business-tabs
// Client-side timeout (ms) enforcing the 30-second bound for lead-scoped Claims binder
// fetch/save requests, so the sub-tab error + retry path surfaces deterministically
// (Requirements 3.5, 3.6).
const LEAD_BINDER_REQUEST_TIMEOUT = 30000;

// Feature: lead-binder-business-tabs
// Module-scoped guard preventing a duplicate lead-scoped Claims save from being
// dispatched while an earlier save for the lead binder is still in flight
// (Requirement 3.8). The customer-policy save path (addClaimAsync) is intentionally not
// gated so its existing behavior remains byte-for-byte unchanged (Requirement 4.2).
let isLeadClaimSaveInProgress = false;

export const ADD_POLICY_ID = "ADD_POLICY_ID";
export const POLICY_BASIC_DETAILS_NOT_FOUND = "POLICY_BASIC_DETAILS_NOT_FOUND";

export const addPolicyId = () => action(ADD_POLICY_ID);

export const FETCH_CUSTOMER_INFO_PROGRESS = "FETCH_CUSTOMER_INFO_PROGRESS";
export const ADD_CUSTOMER_INFO = "ADD_CUSTOMER_INFO";
export const FETCH_CUSTOMER_INFO_FAILED = "FETCH_CUSTOMER_INFO_FAILED";

export const fetchCustomerInfoProgress = () =>
  action(FETCH_CUSTOMER_INFO_PROGRESS);

export const addCustomerInfo = (
  customerInfo: ICustomerInfo,
  customerPolicyId: number,
  isNewPolicy: boolean
) => action(ADD_CUSTOMER_INFO, { customerInfo, customerPolicyId, isNewPolicy });

export const fetchCustomerInfofailed = (errorMessage: string) =>
  action(FETCH_CUSTOMER_INFO_FAILED, { errorMessage });

export const fetchCustomerInfoAsync =
  (
    customerPolicyId: number,
    reload?: boolean
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    const isSaved =
      getState().business.basicDetails.customerInfo.loading ===
      LoadState.Loaded;

    try {
      if (!isSaved || reload) {
        dispatch(fetchCustomerInfoProgress());
        const res = await api.get(
          `/policy/get-policy-business-customer-info?customer_policy_id=${customerPolicyId}`
        );
        const data: ICustomerInfo[] = res.data.data;
        if (data.length > 0) {
          dispatch(
            addCustomerInfo(
              {
                ...data[0],
              },
              customerPolicyId,
              false
            )
          );
        } else {
          dispatch(
            fetchCustomerInfofailed(
              "Oops! We couldn't find any records associated with your policy id at the moment. Please ensure that the provided information is accurate and try again."
            )
          );
        }
      }
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Something went wrong",
          displayAs: "snackbar",
        })
      );
      dispatch(fetchCustomerInfofailed("Something went to be wrong!"));
    }
  };

// Additional Insured

export const FETCH_BUSINESS_ADDITIONAL_INSURED_LIST_PROGRESS =
  "FETCH_BUSINESS_ADDITIONAL_INSURED_LIST_PROGRESS";
export const FETCH_BUSINESS_ADDITIONAL_INSURED_LIST_SUCCESS =
  "FETCH_BUSINESS_ADDITIONAL_INSURED_LIST_SUCCESS";
export const FETCH_BUSINESS_ADDITIONAL_INSURED_LIST_FAILED =
  "FETCH_BUSINESS_ADDITIONAL_INSURED_LIST_FAILED";

export const fetchBusinessAdditionalInsuredListProgress = () =>
  action(FETCH_BUSINESS_ADDITIONAL_INSURED_LIST_PROGRESS);

export const fetchBusinessAdditionalInsuredListSuccess = (
  data: IBusinessAdditionalInsured[]
) => action(FETCH_BUSINESS_ADDITIONAL_INSURED_LIST_SUCCESS, { data });

export const fetchBusinessAdditionalInsuredListFailed = () =>
  action(FETCH_BUSINESS_ADDITIONAL_INSURED_LIST_FAILED);

export const fetchBusinessAdditionalInsuredListAsync =
  (
    customerpolicyId: number,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessAdditionalInsuredListProgress());

      let finalUrl = `/policy/get-policy-business-additional-insured?customer_policy_id=${customerpolicyId}`;

      if (status !== undefined && status !== "-1") {
        finalUrl += `&status=${status}`;
      }
      const res = await api.get(finalUrl);
      const data: IBusinessAdditionalInsured[] = res.data.data;
      dispatch(fetchBusinessAdditionalInsuredListSuccess(data));
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Something went wrong",
          displayAs: "snackbar",
        })
      );
    }
  };
export const upsertBusinessAdditionalInsuredAsync =
  (
    data: IBusinessAdditionalInsured,
    onCallback: (isSucces?: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      await api.post("/policy/edit-policy-business-additional-insured", data);

      onCallback(true);
      dispatch(
        showMessage({
          type: "success",
          message: "Insurer Contact saved successfully!",
          displayAs: "snackbar",
        })
      );
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Something went wrong",
          displayAs: "snackbar",
        })
      );

      onCallback(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const FETCH_PRIOR_POLICY_PROGRESS = "FETCH_PRIOR_POLICY_PROGRESS";
export const ADD_PRIOR_POLICY = "ADD_PRIOR_POLICY";
export const FETCH_PRIOR_POLICY_FAILED = "FETCH_PRIOR_POLICY_FAILED";

export const fetchPriorPolicyProgress = () =>
  action(FETCH_PRIOR_POLICY_PROGRESS);

export const addPriorPolicy = (priorPolicy: IPriorPolicy) =>
  action(ADD_PRIOR_POLICY, { priorPolicy });
export const fetchPriorPolicyFalied = () => action(FETCH_PRIOR_POLICY_FAILED);

export const addPriorPolicyInfoAsync =
  (
    data: IPriorPolicy,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      await api.post("/policy/create-policy-business-prior-policies", {
        ...data,
        status: "ACTIVE",
      });
      dispatch(addPriorPolicy(data));
      dispatch(
        showMessage({
          type: "success",
          message: "Prior policy saved successfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Something went wrong",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };
export const fetchPriorPolicyAsync =
  (customerPolicyId: number): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    const isSaved =
      getState().business.basicDetails.priorPolicy.loading === LoadState.Loaded;

    try {
      if (!isSaved) {
        dispatch(fetchPriorPolicyProgress());
        const res = await api.get(
          `/policy/get-policy-business-prior-policies?customer_policy_id=${customerPolicyId}`
        );
        const data: IPriorPolicy[] = res.data.data;
        if (data.length > 0) {
          dispatch(addPriorPolicy(data[0]));
        } else {
          dispatch(addPriorPolicy(initialBasicDetails["priorPolicy"]["data"]));
        }
      }
    } catch (err: any) {
      dispatch(fetchPriorPolicyFalied());
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Something went wrong",
          displayAs: "snackbar",
        })
      );
    }
  };

export const FETCH_CLAIM_PROGRESS = "FETCH_CLAIM_PROGRESS";
export const ADD_CLAIM = "ADD_CLAIM";
export const FETCH_CLAIM_FAILED = "FETCH_CLAIM_FAILED";
export const CLEAR_BUSINESS_CLAIM_STATE = "CLEAR_BUSINESS_CLAIM_STATE";

export const fetchClaimProgress = () => action(FETCH_CLAIM_PROGRESS);
export const addClaim = (claim: IClaims[]) => action(ADD_CLAIM, { claim });
export const fetchClaimFailed = () => action(FETCH_CLAIM_FAILED);
export const clearBusinessClaimState = () => action(CLEAR_BUSINESS_CLAIM_STATE);

export const addClaimAsync =
  (
    data: IClaims,
    initialData: IClaims,
    isUpdate: boolean,
    onCallback: (isSuccess: boolean, claim?: IClaims) => void,
    isDuplicate?: boolean
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      let res;

      const asPayload = {
        customer_id: data.customer_id,
        policy_type: "BUSINESS",
        customer_policy_id: data.customer_policy_id,
        page_name: "Business Claims",
      };

      const { paths } = await uploadMultipleFile(
        data.file_upload_two || [],
        "POLICY",
        asPayload
      );

      if (isDuplicate) {
        res = await makeApiCall(
          {
            url: "/policy/create-policy-business-claims",
            method: "POST",
            automation: {
              isUpdate: isUpdate,
              primaryFieldName: "customer_policy_id",
            },
          },
          initialData,
          {
            ...data,
            claim_number: null,
            file_upload_two: paths,
          }
        );
      } else {
        res = await makeApiCall(
          {
            url: "/policy/create-policy-business-claims",
            method: "POST",
            automation: {
              isUpdate: isUpdate,
              primaryFieldName: "customer_policy_id",
            },
          },
          initialData,
          {
            ...data,
            file_upload_two: paths,
          }
        );
      }
      dispatch(
        showMessage({
          type: "success",
          message: "Policy claim saved successfully!",
          displayAs: "snackbar",
        })
      );
      const finalResponse = res.data.data;
      onCallback(true, finalResponse);
      dispatch(fetchClaimsAsync(data.customer_policy_id));
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Something went wrong",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// Feature: lead-binder-business-tabs
// Lead-scoped variant of addClaimAsync. Persists a Claims record keyed by lead_id and
// omits customer_policy_id entirely (Requirements 3.2, 3.4), routing the endpoint
// through the centralized map. After a successful save it re-fetches the Claims list by
// the same lead_id so the sub-tab reflects the persisted records (Requirements 3.3,
// 3.7). A module-scoped guard plus saveLoaderProgress/saveLoaderCompleted prevent a
// duplicate in-flight save (Requirement 3.8). Failures / >30s timeouts surface a message
// and retain the broker's entered form input (the form owns its values, so nothing is
// discarded here) (Requirement 3.6). Mirrors the leadAutoApplicationActions precedent.
export const addLeadClaimAsync =
  (
    data: IClaims,
    leadId: string,
    onCallback: (isSuccess: boolean, claim?: IClaims) => void,
    isDuplicate?: boolean,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    if (isLeadClaimSaveInProgress) {
      return;
    }
    isLeadClaimSaveInProgress = true;
    try {
      dispatch(saveLoaderProgress());

      const asPayload = {
        customer_id: data.customer_id,
        policy_type: "BUSINESS",
        page_name: "Business Claims",
      };

      const { paths } = await uploadMultipleFile(
        data.file_upload_two || [],
        "POLICY",
        asPayload
      );

      const { url } = buildBinderRequest(
        "claims",
        { kind: "lead", leadId },
        "save"
      );

      const { customer_policy_id, ...rest } = data as any;
      const res = await api.post(
        url,
        {
          ...rest,
          claim_number: isDuplicate ? null : (rest as IClaims).claim_number,
          file_upload_two: paths,
          lead_id: leadId,
        },
        { timeout: LEAD_BINDER_REQUEST_TIMEOUT }
      );
      dispatch(
        showMessage({
          type: "success",
          message: "Policy claim saved successfully!",
          displayAs: "snackbar",
        })
      );
      const finalResponse = res.data.data;
      onCallback(true, finalResponse);
      // Re-fetch the Claims list by the same lead_id after a successful save
      // (Requirements 3.3, 3.7).
      dispatch(fetchLeadClaimsAsync(leadId, status));
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "The Claims binder record could not be saved. Please retry.",
          displayAs: "snackbar",
        })
      );
    } finally {
      isLeadClaimSaveInProgress = false;
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchClaimsAsync =
  (
    customerPolicyId: number,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchClaimProgress());
      let finalUrl = `/policy/get-policy-business-claims?customer_policy_id=${customerPolicyId}`;

      if (status !== undefined && status !== "-1") {
        finalUrl += `&status=${status}`;
      }
      const res = await api.get(finalUrl);
      const data: IClaims[] = res.data.data;
      dispatch(addClaim(data));
    } catch (err: any) {
      dispatch(fetchClaimFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Something went wrong",
          displayAs: "snackbar",
        })
      );
    }
  };

// Feature: lead-binder-business-tabs
// Lead-scoped variant of fetchClaimsAsync. Fetches the Claims list keyed by lead_id
// (never customer_policy_id) via the centralized endpoint map, so the Claims binder
// sub-tab reads its records by the current lead (Requirements 3.1, 3.4). On failure or
// >30s timeout it flips the shared claim load state to Failed (which the sub-tab
// surfaces as an error + retry) and shows a message (Requirement 3.5). Unsaved broker
// input lives in the form component, not this slice, so it is retained on failure.
// Mirrors the leadAutoApplicationActions precedent.
export const fetchLeadClaimsAsync =
  (
    leadId: string,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchClaimProgress());

      const { url } = buildBinderRequest(
        "claims",
        { kind: "lead", leadId },
        "fetch"
      );

      let finalUrl = url;
      if (status !== undefined && status !== "-1") {
        finalUrl += `&status=${status}`;
      }
      const res = await api.get(finalUrl, {
        timeout: LEAD_BINDER_REQUEST_TIMEOUT,
      });
      const data: IClaims[] = res.data.data;
      dispatch(addClaim(data));
    } catch (err: any) {
      dispatch(fetchClaimFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "The Claims binder section could not be loaded. Please retry.",
          displayAs: "snackbar",
        })
      );
    }
  };

export const FETCH_BUSINESS_CLAIM_PROGRESS = "FETCH_BUSINESS_CLAIM_PROGRESS";
export const FETCH_BUSINESS_CLAIM_SUCCESS = "FETCH_BUSINESS_CLAIM_SUCCESS";
export const FETCH_BUSINESS_CLAIM_FAILED = "FETCH_BUSINESS_CLAIM_FAILED";

export const fetchBusinessClaimProgress = () =>
  action(FETCH_BUSINESS_CLAIM_PROGRESS);
export const fetchBusinessClaimSuccess = (data: IClaims) =>
  action(FETCH_BUSINESS_CLAIM_SUCCESS, {
    data,
  });
export const fetchBusinessClaimFailed = (errorMessage: string) =>
  action(FETCH_BUSINESS_CLAIM_FAILED, { errorMessage });

export const fetchBusinessClaimAsync =
  (
    customerPolicyId: number,
    incremental_number: number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessClaimProgress());
      const res = await api.get(
        `/policy/get-policy-business-claims?customer_policy_id=${customerPolicyId}&column=incremental_number&value=${incremental_number}`
      );
      const data: IClaims[] = res.data.data;
      if (data.length > 0) {
        const attachments: IFileUpload[] = [];
        for (const item of (data[0].file_upload_two || []) as any) {
          if (item) {
            attachments.push({
              file: null,
              key: getUniqueId(),
              path: item,
            });
          }
        }
        dispatch(
          fetchBusinessClaimSuccess({
            ...data[0],
            file_upload_two: attachments,
          })
        );
      } else {
        dispatch(fetchBusinessClaimFailed(ERROR_MESSAGES.POLICY_NOT_FOUND));
      }
    } catch (err: any) {
      dispatch(fetchBusinessClaimFailed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Something went wrong",
          displayAs: "snackbar",
        })
      );
    }
  };

export const FETCH_PREMIUM_PROGRESS = "FETCH_PREMIUM_PROGRESS";
export const ADD_PREMIUM = "ADD_PREMIUM";
export const FETCH_PREMIUM_FAILED = "FETCH_PREMIUM_FAILED";

export const fetchPremiumProgress = () => action(FETCH_PREMIUM_PROGRESS);
export const addPremium = (premium: IPremium) =>
  action(ADD_PREMIUM, { premium });
export const fetchPremiumFailed = () => action(FETCH_PREMIUM_FAILED);

export const addPremiumAsync =
  (
    data: IPremium,
    onCallback: (isSuccess: boolean, error?: any) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      await api.post("/policy/create-policy-business-premium", {
        ...data,
      });
      dispatch(addPremium(data));

      onCallback(true);
    } catch (err: any) {
      onCallback(false, err);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };
export const fetchPremiumAsync =
  (customerPolicyId: number): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    const isSaved =
      getState().business.basicDetails.premium.loading === LoadState.Loaded;

    try {
      if (!isSaved) {
        dispatch(fetchPriorPolicyProgress());
        const res = await api.get(
          `/policy/get-policy-business-premium?customer_policy_id=${customerPolicyId}`
        );
        const data: IPremium[] = res.data.data;
        if (data.length > 0) {
          dispatch(addPremium(data[0]));
        } else {
          dispatch(addPremium(initialBasicDetails["premium"]["data"]));
        }
      }
    } catch (err: any) {
      dispatch(fetchPremiumFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Something went wrong",
          displayAs: "snackbar",
        })
      );
    }
  };

// policy admin

export const FETCH_POLICY_ADMIN_PROGRESS = "FETCH_POLICY_ADMIN_PROGRESS";
export const ADD_POLICY_ADMIN = "ADD_POLICY_ADMIN";
export const FETCH_POLICY_ADMIN_FAILED = "FETCH_POLICY_ADMIN_FAILED";

export const fetchPolicyAdminProgress = () =>
  action(FETCH_POLICY_ADMIN_PROGRESS);
export const addPolicyAdmin = (data: IPolicyAdmin) =>
  action(ADD_POLICY_ADMIN, { data });
export const fetchPolicyAdminFailed = () => action(FETCH_POLICY_ADMIN_FAILED);

export const fetchPolicyAdminAsync =
  (customerPolicyId: number): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    const isSaved =
      getState().business.basicDetails.policyAdmin.loading === LoadState.Loaded;

    try {
      if (!isSaved) {
        dispatch(fetchPolicyAdminProgress());
        const res = await api.get(
          `/policy/get-policy-business-policy-admin?customer_policy_id=${customerPolicyId}`
        );
        const data: IPolicyAdmin[] = res.data.data;
        if (data.length > 0) {
          dispatch(addPolicyAdmin(data[0]));
        } else {
          dispatch(addPolicyAdmin(initialBasicDetails["policyAdmin"]["data"]));
        }
      }
    } catch (err: any) {
      dispatch(fetchPolicyAdminFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Something went wrong",
          displayAs: "snackbar",
        })
      );
    }
  };

// policy admin manager

export const FETCH_BUSINESS_POLICY_ADMIN_MANAGER_PROGRESS =
  "FETCH_BUSINESS_POLICY_ADMIN_MANAGER_PROGRESS";
export const FETCH_BUSINESS_POLICY_ADMIN_MANAGER_SUCCESS =
  "FETCH_BUSINESS_POLICY_ADMIN_MANAGER_SUCCESS";
export const FETCH_BUSINESS_POLICY_ADMIN_MANAGER_FAILED =
  "FETCH_BUSINESS_POLICY_ADMIN_MANAGER_FAILED";

export const fetchBusinessPolicyAdminManagerProgress = () =>
  action(FETCH_BUSINESS_POLICY_ADMIN_MANAGER_PROGRESS);
export const fetchBusinessPolicyAdminManagerSuccess = (
  data: IPolicyAdminManager
) => action(FETCH_BUSINESS_POLICY_ADMIN_MANAGER_SUCCESS, { data });
export const fetchBusinessPolicyAdminManagerFailed = (errorMessage: string) =>
  action(FETCH_BUSINESS_POLICY_ADMIN_MANAGER_FAILED, { errorMessage });

export const addBusinessPolicyAdminManager =
  (
    data: IPolicyAdminManager,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      //dispatch(fetchBusinessPolicyRiskProgress());
      await api.post(
        `/policy/create-policy-business-policy-admin-manager`,
        data
      );
      dispatch(
        showMessage({
          type: "success",
          message: "Policy Admin Manager saved successfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Something went wrong",
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchBusinessPolicyAdminManagerAsync =
  (customerPolicyId: number): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      // if (!isSaved) {
      dispatch(fetchBusinessPolicyAdminManagerProgress());
      const res = await api.get(
        `/policy/get-policy-business-policy-admin-manager?customer_policy_id=${customerPolicyId}`
      );
      const data: IPolicyAdminManager[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchBusinessPolicyAdminManagerSuccess(data[0]));
      } else {
        dispatch(
          fetchBusinessPolicyAdminManagerFailed(
            "Oops! We couldn't find any records associated with your fine art number at the moment. Please ensure that the provided information is accurate and try again."
          )
        );
        // }
      }
    } catch (err: any) {
      dispatch(
        fetchBusinessPolicyAdminManagerFailed(ERROR_MESSAGES.SERVER_ERROR)
      );
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Something went wrong",
          displayAs: "snackbar",
        })
      );
    }
  };

/*--------*/
export const FETCH_BUSINESS_ACTIVITY_LIST_PROGRESS =
  "FETCH_BUSINESS_ACTIVITY_LIST_PROGRESS";
export const FETCH_BUSINESS_ACTIVITY_LIST_SUCCESS =
  "FETCH_BUSINESS_ACTIVITY_LIST_SUCCESS";

export const fetchBusinessActivityListProgress = () =>
  action(FETCH_BUSINESS_ACTIVITY_LIST_PROGRESS);
export const fetchBusinessActivityListSuccess = (data: any) =>
  action(FETCH_BUSINESS_ACTIVITY_LIST_SUCCESS);

export const FETCH_BUSINESS_ATTACHMENT_PROGRESS =
  "FETCH_BUSINESS_ATTACHMENT_PROGRESS";
export const FETCH_BUSINESS_ATTACHMENT_SUCCESS =
  "FETCH_BUSINESS_ATTACHMENT_SUCCESS";
export const FETCH_BUSINESS_ATTACHMENT_FAILED =
  "FETCH_BUSINESS_ATTACHMENT_FAILED";

export const fetchBusinessAttachmentProgress = () =>
  action(FETCH_BUSINESS_ATTACHMENT_PROGRESS);
export const fetchBusinessAttachmentSuccess = (
  data: IBusinessAttachment
  // customerPolicyId: number
) => action(FETCH_BUSINESS_ATTACHMENT_SUCCESS, { data });
export const fetchBusinessAttachmentFailed = (errorMessage: string) =>
  action(FETCH_BUSINESS_ATTACHMENT_FAILED, { errorMessage });

export const addBusinessAttachmentAsync =
  (
    data: IBusinessAttachment,
    policyId: number,
    file: any | null,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const asPayload = {
        customer_id: policyId,
        policy_type: "BUSINESS",
        customer_policy_id: data.customer_policy_id,
        page_name: "Attachment",
      };
      const path = await uploadFile(
        file,
        "POLICY",
        data.file_upload || "",
        asPayload
      );
      await api.post("/policy/edit-policy-business-attachments", {
        ...data,
        file_upload: path,
      });
      // const data = res.data.data;

      dispatch(fetchBusinessAttachmentSuccess(data));
      dispatch(
        showMessage({
          type: "success",
          message: "Attachment saved successfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Something went wrong",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchBusinessAttachmentAsync =
  (customerPolicyId: number): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      // if (!isSaved) {
      dispatch(fetchBusinessAttachmentProgress());
      const res = await api.get(
        `/policy/get-policy-business-attachments?customer_policy_id=${customerPolicyId}`
      );
      const data: IBusinessAttachment[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchBusinessAttachmentSuccess(data[0]));
      } else {
        dispatch(
          fetchBusinessAttachmentFailed(ERROR_MESSAGES.POLICY_NOT_FOUND)
        );
      }
      // }
    } catch (err: any) {
      dispatch(fetchBusinessAttachmentFailed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Something went wrong",
          displayAs: "snackbar",
        })
      );
    }
  };

export const FETCH_BUSINESS_ATTACHMENT_LIST_PROGRESS =
  "FETCH_BUSINESS_ATTACHMENT_LIST_PROGRESS";
export const FETCH_BUSINESS_ATTACHMENT_LIST_SUCCESS =
  "FETCH_BUSINESS_ATTACHMENT_LIST_SUCCESS";
export const FETCH_BUSINESS_ATTACHMENT_LIST_FAILED =
  "FETCH_BUSINESS_ATTACHMENT_LIST_FAILED";

export const fetchBusinessAttachmentListProgress = () =>
  action(FETCH_BUSINESS_ATTACHMENT_LIST_PROGRESS);
export const fetchBusinessAttachmentListSuccess = (
  data: IBusinessAttachment[]
) => action(FETCH_BUSINESS_ATTACHMENT_LIST_SUCCESS, { data });
export const fetchBusinessAttachmentListFailed = () =>
  action(FETCH_BUSINESS_ATTACHMENT_LIST_FAILED);

export const fetchBusinessAttachmentListAsync =
  (
    customerPolicyId: number,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessAttachmentListProgress());

      let finalUrl = `/policy/get-policy-business-attachments?customer_policy_id=${customerPolicyId}`;

      if (status !== undefined && status !== "-1") {
        finalUrl += `&status=${status}`;
      }
      const res = await api.get(finalUrl);
      const data: IBusinessAttachment[] = res.data.data;
      dispatch(fetchBusinessAttachmentListSuccess(data));
    } catch (err: any) {
      dispatch(fetchBusinessAttachmentListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Something went wrong",
          displayAs: "snackbar",
        })
      );
    }
  };

export const addBusinesAttachmentListAsync =
  (
    data: IBusinessAttachment,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      await api.post(`/policy/edit-policy-business-attachments`, data);
      dispatch(
        showMessage({
          type: "success",
          message: "Attachment saved successfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Something went wrong",
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const CLAER_BUSINESS_POLICY_ATTACHMENT_STATE =
  "CLAER_BUSINESS_POLICY_ATTACHMENT_STATE";
export const clearBusinessPolicyAttachment = () =>
  action(CLAER_BUSINESS_POLICY_ATTACHMENT_STATE);

export const CLEAR_BASIC_DETAILS_STATE = "CLEAR_BASIC_DETAILS_STATE";
export const clearBasicPolicyState = () => action(CLEAR_BASIC_DETAILS_STATE);
